import React from "react";
import { IGroup, IStore } from "../../../../classes/interfaces";
import { formatBRL } from "../../../../helpers/currency.helper";
import Icon from "../../../../components/ui/Icon";
import Photo from "../../../../components/ui/Photo";
import { deliveryFeeLabel, isVariableDeliveryFee } from "../../helpers/deliveryFee";
import {
  Wrap,
  Card,
  Cover,
  InfoPanel,
  Avatar,
  Info,
  NameRow,
  Name,
  StatusPill,
  StatusDot,
  Tagline,
  MetaRow,
  MetaDot,
  MetaItem,
} from "./DesktopHero.styles";

interface DesktopHeroProps {
  company: IStore;
  groups?: IGroup[];
  tableId?: string;
  onOpenTaxas: () => void;
  /** Cardapio-vitrine (/menu): esconde prazo, taxa e minimo, como no mobile. */
  isMenu?: boolean;
}

export default function DesktopHero({ company, groups, tableId, onOpenTaxas, isMenu }: DesktopHeroProps) {
  const isOpen = company.status === "opened";
  const isVariableFee = isVariableDeliveryFee(company);
  // Loja que so retira: prazo e taxa de entrega nao se aplicam.
  const isPickupOnly = company?.hasPickup === true && company?.hasDelivery !== true;
  const tagline = (groups ?? [])
    .slice(0, 3)
    .map((g) => g.name)
    .join(" • ");
  const address = `${company.address}, ${company.number} - ${company.district} - ${company.city.name}/${company.city.state}`;
  const phone = `${company.phone}${company.cellPhone ? " / " + company.cellPhone : ""}`;

  // No /menu nao existe entrega nem pedido, entao prazo, taxa e minimo saem
  // inteiros. Numa loja so de retirada, prazo e taxa dao lugar ao aviso.
  const deliveryMeta: Array<{ icon: string; text: string; onClick?: () => void }> = isMenu
    ? []
    : isPickupOnly
      ? [
          { icon: "pin", text: "Somente retirada" },
          {
            icon: "bag",
            text:
              company.minimumSaleValue > 0
                ? `Mín. ${formatBRL(company.minimumSaleValue)}`
                : "Sem mínimo",
          },
        ]
      : [
          {
            icon: "clock",
            text: `${company.initialDeliveryTime}-${company.finalDeliveryTime} min`,
          },
          {
            icon: "moto",
            text: `Taxa ${deliveryFeeLabel(company)}`,
            onClick: isVariableFee ? onOpenTaxas : undefined,
          },
          {
            icon: "bag",
            text:
              company.minimumSaleValue > 0
                ? `Mín. ${formatBRL(company.minimumSaleValue)}`
                : "Sem mínimo",
          },
        ];

  const metaItems: Array<{ icon: string; text: string; onClick?: () => void }> = [
    ...deliveryMeta,
    ...(tableId
      ? [{ icon: "pin", text: `Você está na mesa ${tableId}` }]
      : [
          { icon: "pin", text: address },
          { icon: "phone", text: phone },
        ]),
  ];

  return (
    <Wrap>
      <Card>
        <Cover>
          <Photo src={company.coverImageUrl ?? company.imageUrl} radius={0} objectPosition="center 60%" />
        </Cover>
        <InfoPanel>
          <Avatar>
            <Photo src={company.imageUrl} radius={0} />
          </Avatar>
          <Info>
            <NameRow>
              <Name>{company.name}</Name>
              <StatusPill $open={isOpen}>
                <StatusDot $open={isOpen} />
                {isOpen ? "Aberto agora" : "Fechado"}
              </StatusPill>
            </NameRow>
            {tagline && <Tagline>{tagline}</Tagline>}
            <MetaRow>
              {metaItems.map((item, index) => (
                <React.Fragment key={item.icon}>
                  {index > 0 && <MetaDot />}
                  <MetaItem
                    as={item.onClick ? "button" : "span"}
                    type={item.onClick ? "button" : undefined}
                    $clickable={!!item.onClick}
                    onClick={item.onClick}
                  >
                    <Icon name={item.icon} size={15} color="#8A9893" />
                    {item.text}
                  </MetaItem>
                </React.Fragment>
              ))}
            </MetaRow>
          </Info>
        </InfoPanel>
      </Card>
    </Wrap>
  );
}
